import React from 'react';
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Autocomplete,
  TextField,
} from '@mui/material';
import { useCategories } from 'application/hooks/useCategories';
import { useImportWizardStore } from '../../store/useImportWizardStore';
import ColumnSelect from './ColumnSelect';
import ValueMapEditor from './ValueMapEditor';
import type { MappingConfig } from '../../utils/types';

type DispositionStrategy = MappingConfig['dispositionStrategy'];

const DispositionStrategyConfig: React.FC = () => {
  const headers = useImportWizardStore((s) => s.headers);
  const fileID = useImportWizardStore((s) => s.fileID);
  const categoryID = useImportWizardStore(
    (s) => s.mappingConfig.categoryID ?? ''
  );
  const dispositionStrategy = useImportWizardStore(
    (s) => s.mappingConfig.dispositionStrategy
  );
  const updateMappingConfig = useImportWizardStore(
    (s) => s.updateMappingConfig
  );

  const { categories } = useCategories();
  const selectedCategory = categories.find((cat) => cat.id === categoryID);
  const dispositions = (selectedCategory?.dispositions ?? []).filter(
    (d) => d.id && d.title
  );
  const dispositionOptions = dispositions.map((d) => ({
    id: d.id!,
    title: d.title!,
  }));

  const strategyType = dispositionStrategy?.type ?? 'single_value';

  const getColumn = (): string => {
    if (
      dispositionStrategy &&
      (dispositionStrategy.type === 'column_id' ||
        dispositionStrategy.type === 'column_value_match')
    ) {
      return dispositionStrategy.column;
    }
    return '';
  };

  const getDefaultDispositionID = (): string => {
    if (dispositionStrategy?.type === 'column_id')
      return dispositionStrategy.defaultDispositionID;
    if (dispositionStrategy?.type === 'column_value_match')
      return dispositionStrategy.defaultDispositionID;
    return '';
  };

  const getDispositionID = (): string => {
    if (dispositionStrategy?.type === 'single_value')
      return dispositionStrategy.dispositionID;
    return '';
  };

  const getValueMap = (): Record<string, string> => {
    if (dispositionStrategy?.type === 'column_value_match')
      return dispositionStrategy.valueMap;
    return {};
  };

  const handleStrategyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const type = event.target.value as DispositionStrategy['type'];
    let newStrategy: DispositionStrategy;
    switch (type) {
      case 'column_id':
        newStrategy = {
          type: 'column_id',
          column: '',
          defaultDispositionID: '',
        };
        break;
      case 'column_value_match':
        newStrategy = {
          type: 'column_value_match',
          column: '',
          valueMap: {},
          defaultDispositionID: '',
        };
        break;
      default:
        newStrategy = { type: 'single_value', dispositionID: '' };
    }
    updateMappingConfig({ dispositionStrategy: newStrategy });
  };

  const showColumnSelector =
    strategyType === 'column_id' || strategyType === 'column_value_match';
  const showDefaultDispositionID =
    strategyType === 'column_id' || strategyType === 'column_value_match';
  const showValueMapEditor = strategyType === 'column_value_match';

  return (
    <FormControl component="fieldset" fullWidth>
      <FormLabel component="legend" id="disposition-strategy-label">
        Disposition Assignment Strategy
      </FormLabel>
      <RadioGroup
        aria-labelledby="disposition-strategy-label"
        name="dispositionStrategy"
        value={strategyType}
        onChange={handleStrategyChange}
      >
        <FormControlLabel
          value="single_value"
          control={<Radio />}
          label="Single Value"
        />
        <FormControlLabel
          value="column_id"
          control={<Radio />}
          label="Column (Disposition IDs)"
        />
        <FormControlLabel
          value="column_value_match"
          control={<Radio />}
          label="Column Value Match"
        />
      </RadioGroup>

      {strategyType === 'single_value' && (
        <Box sx={{ mt: 2 }}>
          <Autocomplete
            options={dispositionOptions}
            getOptionLabel={(opt) => opt.title}
            value={
              dispositionOptions.find((d) => d.id === getDispositionID()) ||
              null
            }
            onChange={(_e, val) => {
              updateMappingConfig({
                dispositionStrategy: {
                  type: 'single_value',
                  dispositionID: val?.id || '',
                },
              });
            }}
            renderInput={(params) => (
              <TextField {...params} label="Disposition" size="small" />
            )}
            ListboxProps={{ style: { maxHeight: 200 } }}
            sx={{ maxWidth: 400 }}
            size="small"
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
          />
        </Box>
      )}

      {showColumnSelector && (
        <Box sx={{ mt: 2 }}>
          <ColumnSelect
            label="Disposition Column"
            value={getColumn()}
            headers={headers}
            onChange={(val) => {
              if (strategyType === 'column_id') {
                updateMappingConfig({
                  dispositionStrategy: {
                    type: 'column_id',
                    column: val,
                    defaultDispositionID: getDefaultDispositionID(),
                  },
                });
              } else {
                updateMappingConfig({
                  dispositionStrategy: {
                    type: 'column_value_match',
                    column: val,
                    valueMap: {},
                    defaultDispositionID: getDefaultDispositionID(),
                  },
                });
              }
            }}
          />
        </Box>
      )}

      {showDefaultDispositionID && (
        <Box sx={{ mt: 2 }}>
          <Autocomplete
            options={dispositionOptions}
            getOptionLabel={(opt) => opt.title}
            value={
              dispositionOptions.find(
                (d) => d.id === getDefaultDispositionID()
              ) || null
            }
            onChange={(_e, val) => {
              const newDefault = val?.id || '';
              if (strategyType === 'column_id') {
                updateMappingConfig({
                  dispositionStrategy: {
                    type: 'column_id',
                    column: getColumn(),
                    defaultDispositionID: newDefault,
                  },
                });
              } else {
                updateMappingConfig({
                  dispositionStrategy: {
                    type: 'column_value_match',
                    column: getColumn(),
                    valueMap: getValueMap(),
                    defaultDispositionID: newDefault,
                  },
                });
              }
            }}
            renderInput={(params) => (
              <TextField {...params} label="Default Disposition" size="small" />
            )}
            ListboxProps={{ style: { maxHeight: 200 } }}
            sx={{ maxWidth: 400 }}
            size="small"
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
          />
        </Box>
      )}

      {showValueMapEditor && fileID && getColumn() && (
        <ValueMapEditor
          fileID={fileID}
          columnName={getColumn()}
          valueMap={getValueMap()}
          onChange={(newValueMap) => {
            updateMappingConfig({
              dispositionStrategy: {
                type: 'column_value_match',
                column: getColumn(),
                valueMap: newValueMap,
                defaultDispositionID: getDefaultDispositionID(),
              },
            });
          }}
          options={dispositionOptions}
        />
      )}
    </FormControl>
  );
};

export default DispositionStrategyConfig;
